import { useState, useContext, useRef } from "react";
import {
  Box, Typography, TextField, Button, Divider,
  CircularProgress, Avatar, IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import { AuthContext } from "../context/AuthContext";
import Header from "../components/Header";
import { getMediaUrl, uploadAvatar } from "../service/mediaApi";
import { useSnack } from "../hooks/useSnack";

export default function Profile() {
  const theme = useTheme();
  const { state: user, setState: setUser } = useContext(AuthContext);
  const { showSnack, Snack } = useSnack();
  const col = theme.custom.mymodal;
  const avatarInputRef = useRef(null);

  // Infos profil
  const [pseudo, setPseudo] = useState(user?.users_pseudo || "");
  const [email, setEmail] = useState(user?.users_email || "");
  const [savingProfile, setSavingProfile] = useState(false);

  // Mot de passe
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  // Avatar
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const { user: updatedUser } = await uploadAvatar(file);
      setUser(updatedUser);
      showSnack("Avatar mis à jour !", "success");
    } catch (err) {
      showSnack(err.message || "Erreur upload avatar", "error");
    } finally {
      setUploadingAvatar(false);
      e.target.value = "";
    }
  };

  const handleSaveProfile = async () => {
    if (!pseudo.trim()) { showSnack("Le pseudo ne peut pas être vide.", "warning"); return; }
    setSavingProfile(true);
    try {
      const res = await fetch("/api/profile", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ users_pseudo: pseudo, users_email: email }),
      });
      const data = await res.json();
      if (!res.ok) { showSnack(data.message || "Erreur mise à jour.", "error"); return; }
      setUser(data);
      showSnack("Profil mis à jour !", "success");
    } catch {
      showSnack("Erreur serveur.", "error");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!oldPwd || !newPwd || !confirmPwd) { showSnack("Remplis tous les champs.", "warning"); return; }
    if (newPwd !== confirmPwd) { showSnack("Les nouveaux mots de passe ne correspondent pas.", "warning"); return; }
    setSavingPwd(true);
    try {
      const res = await fetch("/api/change-password", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ old_password: oldPwd, new_password: newPwd }),
      });
      const data = await res.json();
      if (!res.ok) { showSnack(data.message || "Erreur.", "error"); return; }
      showSnack("Mot de passe mis à jour !", "success");
      setOldPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch {
      showSnack("Erreur serveur.", "error");
    } finally {
      setSavingPwd(false);
    }
  };

  const section = {
    backgroundColor: col.main,
    border: col.border,
    borderRadius: "8px",
    p: 3,
    mb: 3,
  };

  const fieldSx = {
    "& input": { color: col.text },
    "& label": { color: col.text + "99" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: col.text + "44" },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: col.text + "88" },
  };

  const avatarSrc = user?.users_avatar_public_id ? getMediaUrl(user.users_avatar_public_id) : undefined;

  return (
    <>
      <Header />
      <Box sx={{ maxWidth: 520, mx: "auto", p: { xs: 2, sm: 3 } }}>

        {/* ── Avatar + pseudo ── */}
        <Box sx={section}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={avatarSrc}
                sx={{ width: 96, height: 96, fontSize: 36, backgroundColor: col.button, color: "#fff" }}
              >
                {!avatarSrc && (user?.users_pseudo?.[0]?.toUpperCase() || "?")}
              </Avatar>
              <IconButton
                size="small"
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
                sx={{
                  position: "absolute", bottom: 0, right: 0,
                  backgroundColor: col.button, color: "#fff",
                  width: 28, height: 28,
                  "&:hover": { backgroundColor: col.button, opacity: 0.85 },
                }}
              >
                {uploadingAvatar ? <CircularProgress size={14} color="inherit" /> : <EditIcon sx={{ fontSize: 14 }} />}
              </IconButton>
            </Box>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleAvatarChange}
            />
            <Typography sx={{ color: col.text, mt: 1.5, fontWeight: 600, fontSize: "1.1rem" }}>
              {user?.users_pseudo}
            </Typography>
            <Typography sx={{ color: col.text, opacity: 0.6, fontSize: "0.85rem" }}>
              {user?.users_email}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: col.text + "22", mb: 2.5 }} />

          <Typography sx={{ color: col.text, fontWeight: 600, mb: 2 }}>Modifier le profil</Typography>

          <TextField
            label="Pseudo" fullWidth value={pseudo}
            onChange={(e) => setPseudo(e.target.value)}
            sx={{ ...fieldSx, mb: 2 }}
          />
          <TextField
            label="Email" type="email" fullWidth value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ ...fieldSx, mb: 2.5 }}
          />

          <Button
            variant="contained"
            onClick={handleSaveProfile}
            disabled={savingProfile}
            startIcon={savingProfile ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ backgroundColor: col.button, color: "#fff" }}
          >
            {savingProfile ? "Sauvegarde…" : "Enregistrer"}
          </Button>
        </Box>

        {/* ── Mot de passe ── */}
        <Box sx={section}>
          <Typography sx={{ color: col.text, fontWeight: 600, mb: 2 }}>Changer le mot de passe</Typography>

          <TextField
            label="Mot de passe actuel" type="password" fullWidth value={oldPwd}
            onChange={(e) => setOldPwd(e.target.value)}
            sx={{ ...fieldSx, mb: 2 }}
          />
          <TextField
            label="Nouveau mot de passe" type="password" fullWidth value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            sx={{ ...fieldSx, mb: 2 }}
          />
          <TextField
            label="Confirmer le nouveau mot de passe" type="password" fullWidth value={confirmPwd}
            onChange={(e) => setConfirmPwd(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleChangePassword(); }}
            sx={{ ...fieldSx, mb: 2.5 }}
          />

          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={savingPwd}
            startIcon={savingPwd ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ backgroundColor: col.button, color: "#fff" }}
          >
            {savingPwd ? "Mise à jour…" : "Changer le mot de passe"}
          </Button>
        </Box>

      </Box>
      {Snack}
    </>
  );
}
